import React, { useState, useEffect } from "react";

const RetirementCorpusCalculator = () => {
  const [monthlyExpense, setMonthlyExpense] = useState(50000);
  const [currentAge, setCurrentAge] = useState(30);
  const [retirementAge, setRetirementAge] = useState(60);
  const [lifeExpectancy, setLifeExpectancy] = useState(85);
  const [inflationRate, setInflationRate] = useState(6);
  const [expectedReturn, setExpectedReturn] = useState(10);
  const [result, setResult] = useState({
    corpusRequired: "0.00",
    monthlyExpenseAtRetirement: "0.00",
    yearsInRetirement: "0",
    monthlySavingsRequired: "0.00",
    inflationAdjustedCorpus: "0.00",
    totalSavings: "0.00",
  });

  // Retirement Corpus Calculation Function
  const calculateRetirement = () => {
    const currentMonthlyExpense = parseFloat(monthlyExpense);
    const age = parseFloat(currentAge);
    const retirement = parseFloat(retirementAge);
    const lifeExp = parseFloat(lifeExpectancy);
    const inflation = parseFloat(inflationRate) / 100;
    const returnRate = parseFloat(expectedReturn) / 100;

    if (isNaN(currentMonthlyExpense) || isNaN(age) || isNaN(retirement) || isNaN(lifeExp) || 
        currentMonthlyExpense <= 0 || retirement <= age || lifeExp <= retirement) return;

    const yearsToRetirement = retirement - age;
    const yearsInRetirement = lifeExp - retirement;

    // Calculate monthly expense at retirement (adjusted for inflation)
    const monthlyExpenseAtRetirement = currentMonthlyExpense * Math.pow(1 + inflation, yearsToRetirement);

    // Calculate annual expense at retirement
    const annualExpenseAtRetirement = monthlyExpenseAtRetirement * 12;

    // Calculate corpus required using present value of annuity formula
    // Assuming withdrawal rate adjusted for inflation during retirement
    const realReturnDuringRetirement = (returnRate - inflation) / (1 + inflation);
    
    // Corpus = Annual Expense × [(1 - (1 + r)^-n) / r]
    // Where r is real return rate and n is years in retirement
    let corpusRequired;
    if (realReturnDuringRetirement > 0) {
      corpusRequired = annualExpenseAtRetirement * 
        ((1 - Math.pow(1 + realReturnDuringRetirement, -yearsInRetirement)) / realReturnDuringRetirement);
    } else {
      corpusRequired = annualExpenseAtRetirement * yearsInRetirement;
    }

    // Calculate monthly savings required to build this corpus
    const monthlyReturnRate = returnRate / 12;
    const monthsToRetirement = yearsToRetirement * 12;
    
    // Using Future Value of Annuity formula: FV = P × [((1 + r)^n - 1) / r]
    // Solving for P: P = FV × [r / ((1 + r)^n - 1)]
    let monthlySavingsRequired;
    if (monthlyReturnRate > 0) {
      monthlySavingsRequired = corpusRequired * 
        (monthlyReturnRate / (Math.pow(1 + monthlyReturnRate, monthsToRetirement) - 1));
    } else {
      monthlySavingsRequired = corpusRequired / monthsToRetirement;
    }

    const totalSavings = monthlySavingsRequired * monthsToRetirement;

    // Calculate inflation-adjusted corpus (today's value)
    const inflationAdjustedCorpus = corpusRequired / Math.pow(1 + inflation, yearsToRetirement);

    setResult({
      corpusRequired: corpusRequired.toFixed(2),
      monthlyExpenseAtRetirement: monthlyExpenseAtRetirement.toFixed(2),
      yearsInRetirement: yearsInRetirement.toString(),
      monthlySavingsRequired: monthlySavingsRequired.toFixed(2),
      inflationAdjustedCorpus: inflationAdjustedCorpus.toFixed(2),
      totalSavings: totalSavings.toFixed(2),
    });
  };

  // Run default calculation on first load
  useEffect(() => {
  calculateRetirement();
}, [monthlyExpense, currentAge, retirementAge, lifeExpectancy, inflationRate, expectedReturn]);


  const yearsToRetirement = retirementAge - currentAge;

  return (
    <div className="calculator-wrapper">
      <div className="calculator-container">
        {/* Header */}
        <div className="calculator-header">
          <div className="header-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: 'white'}}>
              <polyline points="22 7 13.5 15.5 8.5 10.5 2 17"></polyline>
              <polyline points="16 7 22 7 22 13"></polyline>
            </svg>
          </div>
          <h1>Retirement Corpus Calculator</h1>
          <p>
            Calculate how much corpus you need for retirement and how much to save monthly
          </p>
        </div>

        {/* Main Calculator Card */}
        <div className="calculator-card">
          <div className="calculator-grid">
            {/* Left Side - Input Section */}
            <div className="input-section">
              <h2 className="section-title">
                <svg className="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 12V8H6a2 2 0 0 1-2-2c0-1.1.9-2 2-2h12v4"></path>
                  <path d="M4 6v12c0 1.1.9 2 2 2h14v-4"></path>
                  <path d="M18 12a2 2 0 0 0-2 2c0 1.1.9 2 2 2h4v-4h-4z"></path>
                </svg>
                Retirement Details
              </h2>

              {/* Current Monthly Expense */}
              <div className="input-group">
                <label>Current Monthly Expense</label>
                <div className="input-wrapper">
                  <span className="input-prefix">₹</span>
                  <input
                    type="number"
                    value={monthlyExpense}
                    onChange={(e) => setMonthlyExpense(e.target.value)}
                    className="calc-input"
                    placeholder="50000"
                  />
                </div>
                <div className="quick-select-group">
                  {[30000, 50000, 75000, 100000].map((amount) => (
                    <button
                      key={amount}
                      onClick={() => setMonthlyExpense(amount)}
                      className="quick-select-btn primary"
                    >
                      ₹{(amount / 1000).toFixed(0)}K
                    </button>
                  ))}
                </div>
              </div>

              {/* Current Age */}
              <div className="input-group">
                <label>Current Age</label>
                <div className="input-wrapper">
                  <input
                    type="number"
                    value={currentAge}
                    onChange={(e) => setCurrentAge(e.target.value)}
                    className="calc-input"
                    placeholder="30"
                  />
                </div>
                <div className="quick-select-group">
                  {[25, 30, 35, 40].map((age) => (
                    <button
                      key={age}
                      onClick={() => setCurrentAge(age)}
                      className="quick-select-btn secondary"
                    >
                      {age}Y
                    </button>
                  ))}
                </div>
              </div>

              {/* Retirement Age */}
              <div className="input-group">
                <label>Retirement Age</label>
                <div className="input-wrapper">
                  <input
                    type="number"
                    value={retirementAge}
                    onChange={(e) => setRetirementAge(e.target.value)}
                    className="calc-input"
                    placeholder="60"
                  />
                </div>
                <div className="quick-select-group">
                  {[55, 60, 65, 70].map((age) => (
                    <button
                      key={age}
                      onClick={() => setRetirementAge(age)}
                      className="quick-select-btn success"
                    >
                      {age}Y
                    </button>
                  ))}
                </div>
              </div>

              {/* Life Expectancy */}
              <div className="input-group">
                <label>Life Expectancy</label>
                <div className="input-wrapper">
                  <input
                    type="number"
                    value={lifeExpectancy}
                    onChange={(e) => setLifeExpectancy(e.target.value)}
                    className="calc-input"
                    placeholder="85"
                  />
                </div>
                <div className="quick-select-group">
                  {[75, 80, 85, 90].map((age) => (
                    <button
                      key={age}
                      onClick={() => setLifeExpectancy(age)}
                      className="quick-select-btn success"
                    >
                      {age}Y
                    </button>
                  ))}
                </div>
              </div>

              {/* Inflation Rate */}
              <div className="input-group">
                <label>Expected Inflation Rate</label>
                <div className="input-wrapper">
                  <input
                    type="number"
                    value={inflationRate}
                    onChange={(e) => setInflationRate(e.target.value)}
                    className="calc-input"
                    placeholder="6"
                    step="0.1"
                  />
                  <span className="input-suffix">%</span>
                </div>
              </div>

              {/* Expected Return */}
              <div className="input-group">
                <label>Expected Return on Investment</label>
                <div className="input-wrapper">
                  <input
                    type="number"
                    value={expectedReturn}
                    onChange={(e) => setExpectedReturn(e.target.value)}
                    className="calc-input"
                    placeholder="10"
                    step="0.1"
                  />
                  <span className="input-suffix">%</span>
                </div>
                <div className="quick-select-group">
                  {[8, 10, 12, 15].map((rate) => (
                    <button
                      key={rate}
                      onClick={() => setExpectedReturn(rate)}
                      className="quick-select-btn secondary"
                    >
                      {rate}%
                    </button>
                  ))}
                </div>
              </div>

              <button onClick={calculateRetirement} className="calculate-btn">
                Calculate Retirement Corpus
              </button>
            </div>

            {/* Right Side - Results Section */}
            <div className="result-section">
              <h2 className="section-title">
                <svg className="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <circle cx="12" cy="12" r="6"></circle>
                  <circle cx="12" cy="12" r="2"></circle>
                </svg>
                Retirement Summary
              </h2>

              <div className="result-card">
                {/* Corpus Required - Highlighted */}
                <div className="result-item highlight">
                  <div className="result-item-header">
                    <span className="result-label">Corpus Required at {retirementAge}</span>
                    <div className="result-icon-wrapper white">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: 'white'}}>
                        <circle cx="12" cy="12" r="10"></circle>
                        <circle cx="12" cy="12" r="6"></circle>
                        <circle cx="12" cy="12" r="2"></circle>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.corpusRequired).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    To last {result.yearsInRetirement} years of retirement
                  </div>
                </div>

                {/* Monthly Savings Required */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Monthly Savings Required</span>
                    <div className="result-icon-wrapper green">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#10b981'}}>
                        <path d="M20 12V8H6a2 2 0 0 1-2-2c0-1.1.9-2 2-2h12v4"></path>
                        <path d="M4 6v12c0 1.1.9 2 2 2h14v-4"></path>
                        <path d="M18 12a2 2 0 0 0-2 2c0 1.1.9 2 2 2h4v-4h-4z"></path>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.monthlySavingsRequired).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext green">
                    Starting today for {yearsToRetirement} years
                  </div>
                </div>

                {/* Total Savings */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Total Savings Over {yearsToRetirement} Years</span>
                    <div className="result-icon-wrapper blue">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#3b82f6'}}>
                        <polyline points="22 7 13.5 15.5 8.5 10.5 2 17"></polyline>
                        <polyline points="16 7 22 7 22 13"></polyline>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.totalSavings).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    Your total contribution
                  </div>
                </div>

                {/* Monthly Expense at Retirement */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Monthly Expense at {retirementAge}</span>
                    <div className="result-icon-wrapper purple">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#9333ea'}}>
                        <line x1="12" y1="1" x2="12" y2="23"></line>
                        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.monthlyExpenseAtRetirement).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    After {inflationRate}% inflation
                  </div>
                </div>

                {/* Inflation Adjusted Corpus */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Corpus in Today's Value</span>
                    <div className="result-icon-wrapper purple">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#9333ea'}}>
                        <circle cx="12" cy="12" r="10"></circle>
                        <circle cx="12" cy="12" r="6"></circle>
                        <circle cx="12" cy="12" r="2"></circle>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.inflationAdjustedCorpus).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    Real value in current terms
                  </div>
                </div>

                {/* Visual Breakdown */}
                <div className="breakdown-section">
                  <h3 className="breakdown-title">Retirement Timeline</h3>
                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">Years to Retirement</span>
                      <span className="breakdown-percentage">
                        {yearsToRetirement} years
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill blue"
                        style={{
                          width: `${(yearsToRetirement / (yearsToRetirement + Number(result.yearsInRetirement))) * 100}%`,
                        }}
                      />
                    </div>
                  </div>
                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">Years in Retirement</span>
                      <span className="breakdown-percentage green">
                        {result.yearsInRetirement} years
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill green"
                        style={{
                          width: `${(Number(result.yearsInRetirement) / (yearsToRetirement + Number(result.yearsInRetirement))) * 100}%`,
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Info Section */}
        <div className="info-section">
          <div className="info-header">
            <div className="info-icon-wrapper">
              <svg className="info-icon" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="16" x2="12" y2="12"></line>
                <line x1="12" y1="8" x2="12.01" y2="8"></line>
              </svg>
            </div>
            <div>
              <h3>How Does the Retirement Corpus Calculator Work?</h3>
              <p>
                The Retirement Corpus Calculator helps you determine how much money you'll need at 
                retirement to maintain your current lifestyle. It factors in inflation to project 
                your future expenses, calculates the corpus needed to sustain those expenses 
                throughout retirement, and shows you exactly how much you need to save monthly 
                starting today. The calculator uses present value annuity formulas to ensure your 
                corpus lasts through your expected lifespan.
              </p>
            </div>
          </div>

          <div className="formula-section">
            <h4>Key Formulas Used:</h4>
            <div className="formula-display">
              Corpus = Annual Expense × [(1 - (1 + r)^-n) / r]
            </div>

            <div className="formula-params">
              <div className="formula-param">
                <div className="formula-param-key">Corpus</div>
                <div className="formula-param-value">Total Amount Needed</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">r</div>
                <div className="formula-param-value">Real Return Rate (Return - Inflation)</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">n</div>
                <div className="formula-param-value">Years in Retirement</div>
              </div>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="faq-section">
            <h3>Frequently Asked Questions</h3>

            <div className="faq-item">
              <h5>How much corpus do I need for retirement?</h5>
              <p>
                The corpus needed depends on your monthly expenses, retirement age, life expectancy, 
                and inflation rate. A common rule of thumb suggests you need 25-30 times your annual 
                expenses. For example, if you need ₹1 lakh monthly (₹12 lakh annually), you'd need 
                ₹3-3.6 crore. However, this varies based on your lifestyle, healthcare needs, and 
                other factors. Use this calculator to get a personalized estimate.
              </p>
            </div>

            <div className="faq-item">
              <h5>What is the 4% withdrawal rule?</h5>
              <p>
                The 4% rule suggests you can safely withdraw 4% of your retirement corpus annually, 
                adjusted for inflation, without running out of money for 30 years. This means if you 
                have ₹1 crore, you can withdraw ₹4 lakh in the first year, and increase withdrawals 
                by inflation rate each subsequent year. This calculator uses similar principles but 
                adjusts based on your specific parameters like expected returns and inflation.
              </p>
            </div>

            <div className="faq-item">
              <h5>Should I include inflation in retirement planning?</h5>
              <p>
                Absolutely. Inflation is critical for retirement planning as it erodes purchasing 
                power over time. At 6% inflation, prices double every 12 years. What costs ₹1 lakh 
                today will cost ₹3.2 lakh in 20 years. This calculator factors inflation to show 
                both future values and today's equivalent values, helping you understand real 
                purchasing power. Always use conservative inflation estimates (6-7% for India).
              </p>
            </div>

            <div className="faq-item">
              <h5>What should be my asset allocation for retirement?</h5>
              <p>
                Asset allocation should shift from aggressive to conservative as you near retirement. 
                A common rule is to subtract your age from 100 or 110 to determine equity percentage. 
                For example, at age 30, keep 70-80% in equity and 20-30% in debt. At 60, shift to 
                40-50% equity and 50-60% debt. Post-retirement, maintain 30-40% equity for growth 
                while keeping enough in debt/liquid funds to cover 3-5 years of expenses.
              </p>
            </div>

            <div className="faq-item">
              <h5>How do I account for healthcare costs in retirement?</h5>
              <p>
                Healthcare is often the biggest unknown in retirement planning. Medical costs inflate 
                faster than general inflation (10-15% annually in India). Ensure you have 
                comprehensive health insurance with adequate coverage (₹10-25 lakh per person). 
                Additionally, set aside 20-30% of your retirement corpus specifically for healthcare, 
                or add ₹10,000-20,000 to your monthly expense estimate to cover health insurance 
                premiums and out-of-pocket medical costs.
              </p>
            </div>

            <div className="faq-item">
              <h5>What if I start saving late for retirement?</h5>
              <p>
                Starting late means you need to save significantly more monthly due to less time for 
                compounding. If you're in your 40s or 50s, consider maximizing contributions to 
                tax-advantaged accounts like PPF, NPS, and ELSS. Delay retirement by a few years if 
                possible. Cut unnecessary expenses and redirect funds to retirement savings. Even 
                starting at 40, you can build a meaningful corpus with disciplined investing. The 
                key is to start now, regardless of age.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RetirementCorpusCalculator;